import React, { useEffect, useRef, useState } from 'react';
import { View, Text, TextInput, Pressable, ScrollView, Animated, Easing, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import FilterChecklistDropdown from './FilterChecklistDropdown';
import { filterTokens, focusRing, webTransition, usePrefersReducedMotion, isWeb } from './mapFilterUi';
import {
  toggleFilterValue,
  countActiveFilters,
  countGroupFilters,
} from '../../utils/venueFilters';

const DRAWER_WIDTH = 360;
const FOCUSABLE = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

function capacitySummary(filters) {
  const min = String(filters?.capacityMin || '').trim();
  const max = String(filters?.capacityMax || '').trim();
  if (min && max) return `${min}–${max}`;
  if (min) return `${min}+`;
  if (max) return `Up to ${max}`;
  return 'All';
}

function groupSummary(group, filters) {
  if (group.kind === 'range') return capacitySummary(filters);
  const n = countGroupFilters(filters, group);
  return n === 0 ? 'All' : `${n} selected`;
}

/**
 * REQ-104: the map's Filter drawer (AMTN-shaped: search, one checklist per topic, Clear / Show).
 * Presentational: the parent owns the (draft) filter state and the result count.
 *
 * props: open, onClose, groups, filters, onChange(nextFilters), onClear, resultCount, onApply,
 *        searchQuery, onSearchChange, isDarkMode, isMobile, left, returnFocusRef
 */
export default function VenueFilterDrawer({
  open = false,
  onClose,
  groups = [],
  filters,
  onChange,
  onClear,
  resultCount = 0,
  onApply,
  searchQuery = '',
  onSearchChange,
  isDarkMode = false,
  isMobile = false,
  left = 0,
  returnFocusRef = null,
}) {
  const reduced = usePrefersReducedMotion();
  const t = filterTokens(isDarkMode);
  const styles = makeStyles(t, isDarkMode, isMobile);
  const panelRef = useRef(null);
  const closeRef = useRef(null);
  const anim = useRef(new Animated.Value(0)).current;
  const [openGroups, setOpenGroups] = useState({});
  const [focusedInput, setFocusedInput] = useState(null);

  // Open the groups that already have picks each time the drawer opens.
  useEffect(() => {
    if (!open) return;
    const initial = {};
    groups.forEach((g) => { if (countGroupFilters(filters, g) > 0) initial[g.id] = true; });
    setOpenGroups(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) {
      anim.setValue(0);
      return undefined;
    }
    if (reduced) {
      anim.setValue(1);
    } else {
      Animated.timing(anim, { toValue: 1, duration: 220, easing: Easing.out(Easing.cubic), useNativeDriver: false }).start();
    }
    const focusTimer = setTimeout(() => closeRef.current?.focus?.(), 30);
    return () => clearTimeout(focusTimer);
  }, [open, reduced, anim]);

  // Escape closes; Tab stays inside the drawer; focus goes back to the Filter button on close.
  useEffect(() => {
    if (!open || !isWeb || typeof document === 'undefined') return undefined;
    const returnTo = returnFocusRef;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose?.();
        return;
      }
      if (e.key !== 'Tab') return;
      const node = panelRef.current;
      if (!node?.querySelectorAll) return;
      const items = Array.from(node.querySelectorAll(FOCUSABLE)).filter((el) => !el.disabled && el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      } else if (!node.contains(document.activeElement)) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      setTimeout(() => returnTo?.current?.focus?.(), 0);
    };
  }, [open, onClose, returnFocusRef]);

  if (!open || !filters) return null;

  // The footer counts and clears only what this drawer shows; Resources rail picks still narrow the result.
  const activeCount = countActiveFilters({ ...filters, resources: [], subscriptionFeatures: [] });
  const hasCriteria = countActiveFilters(filters) > 0 || Boolean(String(searchQuery || '').trim());
  const primaryLabel = hasCriteria
    ? `Show ${resultCount.toLocaleString()} ${resultCount === 1 ? 'venue' : 'venues'}`
    : 'Done';
  const toggleGroup = (id) => setOpenGroups((prev) => ({ ...prev, [id]: !prev[id] }));
  const set = (next) => onChange?.(next);

  const renderGroup = (group) => {
    const common = {
      key: group.id,
      id: group.id,
      label: group.label,
      summary: groupSummary(group, filters),
      count: countGroupFilters(filters, group),
      open: Boolean(openGroups[group.id]),
      onToggleOpen: () => toggleGroup(group.id),
      note: group.note || null,
      isDarkMode,
      reducedMotion: reduced,
    };

    if (group.kind === 'range') {
      const inputStyle = (name) => [styles.input, focusedInput === name && styles.inputFocused];
      const onNum = (key) => (text) => set({ ...filters, [key]: String(text || '').replace(/[^\d]/g, '') });
      return (
        <FilterChecklistDropdown
          {...common}
          rows={[{
            key: 'unknown',
            label: 'Include venues with unknown capacity',
            checked: Boolean(filters.showUnknownCapacity),
            onPress: () => set({ ...filters, showUnknownCapacity: !filters.showUnknownCapacity }),
          }]}
        >
          <View style={styles.rangeRow}>
            <View style={styles.rangeField}>
              <Text style={styles.rangeLabel} nativeID="venue-filter-cap-min">Min</Text>
              <TextInput
                value={String(filters.capacityMin || '')}
                onChangeText={onNum('capacityMin')}
                placeholder="0"
                placeholderTextColor={isDarkMode ? 'rgba(255,255,255,0.32)' : '#a8a8a2'}
                keyboardType="number-pad"
                inputMode="numeric"
                accessibilityLabel="Minimum capacity"
                onFocus={() => setFocusedInput('min')}
                onBlur={() => setFocusedInput(null)}
                style={inputStyle('min')}
              />
            </View>
            <Text style={styles.rangeDash} accessibilityElementsHidden>–</Text>
            <View style={styles.rangeField}>
              <Text style={styles.rangeLabel}>Max</Text>
              <TextInput
                value={String(filters.capacityMax || '')}
                onChangeText={onNum('capacityMax')}
                placeholder="Any"
                placeholderTextColor={isDarkMode ? 'rgba(255,255,255,0.32)' : '#a8a8a2'}
                keyboardType="number-pad"
                inputMode="numeric"
                accessibilityLabel="Maximum capacity"
                onFocus={() => setFocusedInput('max')}
                onBlur={() => setFocusedInput(null)}
                style={inputStyle('max')}
              />
            </View>
          </View>
        </FilterChecklistDropdown>
      );
    }

    const rows = [];
    if (group.parent) {
      const { group: pg, value: pv, label: pl } = group.parent;
      rows.push({
        key: `${pg}:${pv}`,
        label: pl,
        emphasis: true,
        checked: Boolean(filters[pg]?.includes(pv)),
        onPress: () => set(toggleFilterValue(filters, pg, pv)),
      });
    }
    (group.options || []).forEach((opt) => {
      rows.push({
        key: opt.value,
        label: opt.label,
        checked: Boolean(filters[group.id]?.includes(opt.value)),
        onPress: () => set(toggleFilterValue(filters, group.id, opt.value)),
      });
    });
    return <FilterChecklistDropdown {...common} rows={rows} />;
  };

  const panelMotion = {
    opacity: anim,
    transform: [{ translateX: anim.interpolate({ inputRange: [0, 1], outputRange: [-16, 0] }) }],
  };

  return (
    <View style={[StyleSheet.absoluteFill, styles.layer]} pointerEvents="box-none">
      <Pressable
        style={[StyleSheet.absoluteFill, styles.scrim]}
        onPress={onClose}
        accessibilityLabel="Close filters"
        {...(isWeb ? { tabIndex: -1 } : {})}
      />
      <Animated.View
        ref={panelRef}
        style={[styles.panel, { left: isMobile ? 0 : left }, panelMotion]}
        accessibilityRole={isWeb ? undefined : 'none'}
        accessibilityViewIsModal
        {...(isWeb ? { role: 'dialog', 'aria-modal': true, 'aria-labelledby': 'venue-filter-title' } : {})}
      >
        <View style={styles.head}>
          <View>
            <Text style={styles.eyebrow}>Map</Text>
            <Text style={styles.title} nativeID="venue-filter-title" accessibilityRole="header">Filter venues</Text>
          </View>
          <Pressable
            ref={closeRef}
            onPress={onClose}
            accessibilityRole="button"
            accessibilityLabel="Close filters"
            style={({ hovered, focused }) => [styles.close, hovered && styles.closeHover, webTransition(reduced), focusRing(focused, isDarkMode)]}
          >
            <Ionicons name="close" size={18} color={t.pal.ink} />
          </Pressable>
        </View>

        <View style={styles.searchWrap}>
          <Ionicons name="search" size={15} color={isDarkMode ? 'rgba(255,255,255,0.48)' : t.pal.mute} />
          <TextInput
            value={searchQuery || ''}
            onChangeText={onSearchChange}
            placeholder="Search venue or city"
            placeholderTextColor={isDarkMode ? 'rgba(255,255,255,0.38)' : '#9a9a94'}
            accessibilityLabel="Search venues"
            onFocus={() => setFocusedInput('search')}
            onBlur={() => setFocusedInput(null)}
            style={[styles.searchInput, isWeb && { outlineStyle: 'none' }]}
          />
          {searchQuery ? (
            <Pressable
              onPress={() => onSearchChange?.('')}
              accessibilityRole="button"
              accessibilityLabel="Clear search"
              style={({ focused }) => [styles.searchClear, focusRing(focused, isDarkMode)]}
            >
              <Ionicons name="close-circle" size={16} color={isDarkMode ? 'rgba(255,255,255,0.45)' : '#a8a8a2'} />
            </Pressable>
          ) : null}
          {focusedInput === 'search' ? <View style={styles.searchFocusBar} pointerEvents="none" /> : null}
        </View>

        <Text style={styles.sectionLabel}>Filter by</Text>
        <ScrollView style={styles.scroll} contentContainerStyle={styles.scrollContent} keyboardShouldPersistTaps="handled">
          {groups.map(renderGroup)}
        </ScrollView>

        <View style={styles.foot}>
          <Text style={styles.liveCount} accessibilityLiveRegion="polite" {...(isWeb ? { 'aria-live': 'polite' } : {})}>
            {activeCount > 0 ? `${activeCount} ${activeCount === 1 ? 'filter' : 'filters'} on` : 'No filters on'}
          </Text>
          <View style={styles.footButtons}>
            {activeCount > 0 ? (
              <Pressable
                onPress={onClear}
                accessibilityRole="button"
                accessibilityLabel="Clear all filters"
                style={({ hovered, focused }) => [styles.clearBtn, hovered && styles.closeHover, webTransition(reduced), focusRing(focused, isDarkMode)]}
              >
                <Text style={styles.clearText}>Clear</Text>
              </Pressable>
            ) : null}
            <Pressable
              onPress={onApply}
              accessibilityRole="button"
              accessibilityLabel={primaryLabel}
              style={({ hovered, focused, pressed }) => [
                styles.applyBtn,
                hovered && styles.applyHover,
                pressed && !reduced && { transform: [{ scale: 0.98 }] },
                webTransition(reduced),
                focusRing(focused, isDarkMode),
              ]}
            >
              <Text style={styles.applyText}>{primaryLabel}</Text>
            </Pressable>
          </View>
        </View>
      </Animated.View>
    </View>
  );
}

function makeStyles(t, isDarkMode, isMobile) {
  return StyleSheet.create({
    layer: { zIndex: 40 },
    scrim: {
      backgroundColor: isDarkMode ? 'rgba(0,0,0,0.38)' : 'rgba(20,20,26,0.16)',
      ...(isWeb ? { cursor: 'default' } : {}),
    },
    panel: {
      position: 'absolute',
      top: 0,
      bottom: 0,
      ...(isMobile ? { right: 0 } : { width: DRAWER_WIDTH, maxWidth: '92%' }),
      backgroundColor: t.panel,
      borderRightWidth: isMobile ? 0 : 1,
      borderRightColor: t.pal.line,
      ...(isWeb ? { boxShadow: isDarkMode ? '12px 0 32px rgba(0,0,0,0.45)' : '12px 0 32px rgba(20,20,26,0.10)' } : {}),
    },
    head: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      paddingTop: 18,
      paddingHorizontal: 18,
      paddingBottom: 12,
    },
    eyebrow: {
      fontFamily: t.mono,
      fontSize: 10,
      letterSpacing: 1.3,
      textTransform: 'uppercase',
      color: t.greenText,
      marginBottom: 2,
    },
    title: {
      fontFamily: t.sans,
      fontSize: 20,
      fontWeight: '700',
      letterSpacing: -0.36,
      color: t.pal.ink,
    },
    close: {
      width: 36,
      height: 36,
      borderRadius: 6,
      alignItems: 'center',
      justifyContent: 'center',
      marginRight: -6,
      ...(isWeb ? { cursor: 'pointer' } : {}),
    },
    closeHover: { backgroundColor: t.hover },
    searchWrap: {
      marginHorizontal: 18,
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      height: 40,
      paddingHorizontal: 12,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: t.pal.line,
      backgroundColor: t.pal.sunk,
      overflow: 'hidden',
    },
    searchInput: {
      flex: 1,
      minWidth: 0,
      backgroundColor: 'transparent',
      height: 38,
      fontFamily: t.sans,
      fontSize: 14,
      color: t.pal.ink,
    },
    searchClear: { padding: 4, borderRadius: 999 },
    searchFocusBar: {
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 0,
      height: 2,
      backgroundColor: t.green,
    },
    sectionLabel: {
      marginTop: 18,
      marginBottom: 4,
      paddingHorizontal: 18,
      fontFamily: t.mono,
      fontSize: 10.5,
      letterSpacing: 1.3,
      textTransform: 'uppercase',
      color: isDarkMode ? 'rgba(255,255,255,0.5)' : t.pal.muteSoft,
    },
    scroll: { flex: 1 },
    scrollContent: { paddingHorizontal: 14, paddingBottom: 16 },
    rangeRow: { flexDirection: 'row', alignItems: 'flex-end', gap: 8, paddingHorizontal: 6, paddingBottom: 8 },
    rangeField: { flex: 1, gap: 4 },
    rangeLabel: {
      fontFamily: t.mono,
      fontSize: 10,
      letterSpacing: 1,
      textTransform: 'uppercase',
      color: isDarkMode ? 'rgba(255,255,255,0.5)' : t.pal.mute,
    },
    rangeDash: { paddingBottom: 10, color: t.pal.mute },
    input: {
      height: 38,
      paddingHorizontal: 10,
      borderRadius: 6,
      borderWidth: 1,
      borderColor: t.pal.line,
      backgroundColor: t.pal.sunk,
      fontFamily: t.mono,
      fontSize: 14,
      color: t.pal.ink,
      ...(isWeb ? { outlineStyle: 'none' } : {}),
    },
    inputFocused: { borderColor: t.green, borderWidth: 2, paddingHorizontal: 9 },
    foot: {
      borderTopWidth: 1,
      borderTopColor: t.pal.line,
      paddingHorizontal: 18,
      paddingTop: 12,
      // Phones: clear the floating Day Planner pill, which sits above the map stage.
      paddingBottom: isMobile ? 76 : 16,
      gap: 10,
      backgroundColor: t.panel,
    },
    liveCount: {
      fontFamily: t.mono,
      fontSize: 10.5,
      letterSpacing: 1,
      textTransform: 'uppercase',
      color: isDarkMode ? 'rgba(255,255,255,0.5)' : t.pal.muteSoft,
    },
    footButtons: { flexDirection: 'row', alignItems: 'center', gap: 8 },
    clearBtn: {
      height: 44,
      paddingHorizontal: 16,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: t.pal.line,
      alignItems: 'center',
      justifyContent: 'center',
      ...(isWeb ? { cursor: 'pointer' } : {}),
    },
    clearText: { fontFamily: t.sans, fontSize: 14, fontWeight: '600', color: t.pal.ink },
    applyBtn: {
      flex: 1,
      height: 44,
      borderRadius: 8,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: t.fill,
      ...(isWeb ? { cursor: 'pointer' } : {}),
    },
    applyHover: { backgroundColor: t.fillHover },
    applyText: { fontFamily: t.sans, fontSize: 14, fontWeight: '700', color: '#fffffe', letterSpacing: 0.1 },
  });
}
